import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  FormControlLabel,
  Alert,
  Typography,
  Box,
  Divider,
  Paper,
  Switch,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClasses } from '../../slices/classesSlice';
import { fetchStudents } from '../../slices/studentsSlice';

const SectionHeader = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
    <Typography variant="subtitle1" fontWeight={600} color="primary.main">
      {title}
    </Typography>
  </Box>
);

const SectionCard = ({ children, sx = {} }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2.5,
      borderRadius: 2,
      borderColor: 'divider',
      background: (theme) =>
        theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const EventDialog = ({ open, onClose, onSave, event }) => {
  const dispatch = useDispatch();
  const { list: classes } = useSelector((state) => state.classes || { list: [] });
  const { students } = useSelector((state) => state.students || { students: [] });
  const safeStudents = students || [];

  const [formData, setFormData] = useState({
    title: '',
    event_type: '',
    description: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    location: '',
    estimated_cost: '',
    student_fee: '',
    target_audience: 'all',
    classes: [],
    students: [],
    requires_registration: false,
    max_participants: '',
    registration_deadline: '',
    send_reminder: true,
    reminder_days_before: 1,
    is_published: true,
  });

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        event_type: event.event_type || '',
        description: event.description || '',
        start_date: event.start_date || '',
        end_date: event.end_date || '',
        start_time: event.start_time || '',
        end_time: event.end_time || '',
        location: event.location || '',
        estimated_cost: event.estimated_cost || '',
        student_fee: event.student_fee || '',
        target_audience: event.target_audience || 'all',
        classes: event.classes?.map(c => c.id || c) || [],
        students: event.students?.map(s => s.id || s) || [],
        requires_registration: event.requires_registration || false,
        max_participants: event.max_participants || '',
        registration_deadline: event.registration_deadline || '',
        send_reminder: event.send_reminder !== undefined ? event.send_reminder : true,
        reminder_days_before: event.reminder_days_before || 1,
        is_published: event.is_published !== undefined ? event.is_published : true,
      });
    } else {
      setFormData({
        title: '',
        event_type: '',
        description: '',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        location: '',
        estimated_cost: '',
        student_fee: '',
        target_audience: 'all',
        classes: [],
        students: [],
        requires_registration: false,
        max_participants: '',
        registration_deadline: '',
        send_reminder: true,
        reminder_days_before: 1,
        is_published: true,
      });
    }
  }, [event, open]);

  useEffect(() => {
    if (open && classes.length === 0) {
      dispatch(fetchClasses());
    }
  }, [open, classes.length, dispatch]);

  useEffect(() => {
    if (open && formData.target_audience === 'specific_classes' && safeStudents.length === 0) {
      dispatch(fetchStudents({ is_archived: false }));
    }
  }, [open, formData.target_audience, safeStudents.length, dispatch]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleSwitch = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.checked });
  };

  const handleClassesChange = (e) => {
    setFormData({ ...formData, classes: e.target.value });
  };

  const handleStudentsChange = (e) => {
    setFormData({ ...formData, students: e.target.value });
  };

  const handleSubmit = () => {
    const submitData = {
      ...formData,
      max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
      reminder_days_before: parseInt(formData.reminder_days_before),
      student_fee: formData.student_fee !== '' ? formData.student_fee : null,
    };
    onSave(submitData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          pt: 2.5,
          px: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          {event ? 'تعديل فعالية' : 'إضافة فعالية جديدة'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Section 1: Basic Info */}
          <SectionCard>
            <SectionHeader icon={<EventIcon />} title="المعلومات الأساسية" />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="عنوان الفعالية"
                  value={formData.title}
                  onChange={handleChange('title')}
                  placeholder="أدخل عنوان الفعالية"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>نوع الفعالية</InputLabel>
                  <Select
                    value={formData.event_type}
                    label="نوع الفعالية"
                    onChange={handleChange('event_type')}
                  >
                    <MenuItem value="">اختر نوع الفعالية</MenuItem>
                    <MenuItem value="assembly">تجمع</MenuItem>
                    <MenuItem value="holiday">عطلة</MenuItem>
                    <MenuItem value="exam_period">فترة امتحانات</MenuItem>
                    <MenuItem value="trip">رحلة</MenuItem>
                    <MenuItem value="celebration">احتفال</MenuItem>
                    <MenuItem value="meeting">اجتماع</MenuItem>
                    <MenuItem value="workshop">ورشة عمل</MenuItem>
                    <MenuItem value="sports_day">يوم رياضي</MenuItem>
                    <MenuItem value="graduation">تخرج</MenuItem>
                    <MenuItem value="other">أخرى</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>الفئة المستهدفة</InputLabel>
                  <Select
                    value={formData.target_audience}
                    label="الفئة المستهدفة"
                    onChange={handleChange('target_audience')}
                  >
                    <MenuItem value="all">الكل</MenuItem>
                    <MenuItem value="students">الطلاب</MenuItem>
                    <MenuItem value="staff">الموظفون</MenuItem>
                    <MenuItem value="parents">أولياء الأمور</MenuItem>
                    <MenuItem value="specific_classes">صفوف محددة</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.target_audience === 'specific_classes' && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>الصفوف</InputLabel>
                      <Select
                        multiple
                        value={formData.classes}
                        label="الصفوف"
                        onChange={handleClassesChange}
                      >
                        {classes.map((cls) => (
                          <MenuItem key={cls.id} value={cls.id}>
                            {cls.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>طلاب محددون (اختياري)</InputLabel>
                      <Select
                        multiple
                        value={formData.students}
                        label="طلاب محددون (اختياري)"
                        onChange={handleStudentsChange}
                      >
                        {safeStudents
                          .filter(s => !s.is_archived)
                          .map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {`${s.first_name} ${s.second_name || ''}`.trim()}
                              {s.school_class_name ? ` — ${s.school_class_name}` : ''}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="الوصف"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleChange('description')}
                  placeholder="أدخل وصفاً للفعالية (اختياري)"
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* Section 2: Date & Time */}
          <SectionCard>
            <SectionHeader icon={<AccessTimeIcon />} title="التاريخ والوقت" />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="تاريخ البداية"
                  type="date"
                  value={formData.start_date}
                  onChange={handleChange('start_date')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="تاريخ النهاية"
                  type="date"
                  value={formData.end_date}
                  onChange={handleChange('end_date')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="وقت البداية"
                  type="time"
                  value={formData.start_time}
                  onChange={handleChange('start_time')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="وقت النهاية"
                  type="time"
                  value={formData.end_time}
                  onChange={handleChange('end_time')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {/* Section 3: Location & Finance */}
          <SectionCard>
            <SectionHeader icon={<LocationOnIcon />} title="المكان والتكاليف" />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="المكان"
                  value={formData.location}
                  onChange={handleChange('location')}
                  placeholder="أدخل مكان الفعالية"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="التكلفة المقدرة (₪)"
                  type="number"
                  value={formData.estimated_cost}
                  onChange={handleChange('estimated_cost')}
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="رسوم الطالب (₪)"
                  type="number"
                  value={formData.student_fee}
                  onChange={handleChange('student_fee')}
                  helperText="تُضاف تلقائياً لرسوم الطالب عند تسجيله"
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>
              {formData.student_fee && parseFloat(formData.student_fee) > 0 && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ py: 0.5, borderRadius: 2 }}>
                    سيتم إضافة مبلغ {formData.student_fee}₪ تلقائياً لرصيد رسوم كل طالب يُسجَّل في هذه الفعالية.
                  </Alert>
                </Grid>
              )}
            </Grid>
          </SectionCard>

          {/* Section 4: Registration & Notifications */}
          <SectionCard>
            <SectionHeader icon={<HowToRegIcon />} title="التسجيل والتذكير" />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2">يتطلب تسجيل</Typography>
                  <Switch
                    checked={formData.requires_registration}
                    onChange={handleSwitch('requires_registration')}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2">إرسال تذكير</Typography>
                  <Switch
                    checked={formData.send_reminder}
                    onChange={handleSwitch('send_reminder')}
                    size="small"
                  />
                </Box>
              </Grid>
              {formData.requires_registration && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="الحد الأقصى للمشاركين"
                      type="number"
                      value={formData.max_participants}
                      onChange={handleChange('max_participants')}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="آخر موعد للتسجيل"
                      type="date"
                      value={formData.registration_deadline}
                      onChange={handleChange('registration_deadline')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
              {formData.send_reminder && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="عدد الأيام قبل الفعالية"
                    type="number"
                    value={formData.reminder_days_before}
                    onChange={handleChange('reminder_days_before')}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              )}
            </Grid>
          </SectionCard>

          {/* Publishing toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              border: '1px solid',
              borderColor: formData.is_published ? 'success.light' : 'divider',
              borderRadius: 2,
              bgcolor: formData.is_published ? 'success.50' : 'transparent',
              transition: 'all 0.2s',
            }}
          >
            <Box>
              <Typography variant="body1" fontWeight={500}>
                نشر الفعالية
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formData.is_published ? 'الفعالية منشورة وظاهرة للجميع' : 'الفعالية مخفية (مسودة)'}
              </Typography>
            </Box>
            <Switch
              checked={formData.is_published}
              onChange={handleSwitch('is_published')}
              color="success"
            />
          </Box>

        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1,
        }}
      >
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, px: 3 }}>
          إلغاء
        </Button>
        <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: 2, px: 3 }}>
          {event ? 'حفظ التعديلات' : 'إضافة الفعالية'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventDialog;
